import { useQuery } from "@apollo/client";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  CreateTagGroupMutation,
  CreateTagGroupMutationVariables,
  CreateTagMutation,
  CreateTagMutationVariables,
  DeleteTagGroupsMutation,
  DeleteTagGroupsMutationVariables,
  DeleteTagsMutation,
  DeleteTagsMutationVariables,
  EditTagGroupMutation,
  EditTagGroupMutationVariables,
  EditTagMutation,
  EditTagMutationVariables,
  IntegrationServiceType,
  ListTagsAndGroupsWithIntegrationsQuery,
  ListTagsAndGroupsWithIntegrationsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { DeleteManyState, TableBox } from "~frontend/components/Table";
import {
  CREATE_TAG,
  EDIT_TAG,
  LIST_TAGS_WITH_INTEGRATIONS,
  LIST_TAGS_AND_GROUPS_WITH_INTEGRATIONS,
  DELETE_TAGS,
  DELETE_TAG_GROUPS,
  CREATE_TAG_GROUP,
  TagDetail,
  TagGroupDetail,
  EDIT_TAG_GROUP,
} from "~frontend/graphql/tags";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import CreateTagOrGroupDialog from "./CreateTagOrGroupDialog";
import EditTagDialog from "./EditTagDialog";
import EditTagGroupDialog from "./EditTagGroupDialog";
import TagListControls from "./TagListControls";
import TagTable from "./TagTable";
import { TAG_SORTS, usePaginatedTags } from "./pagination";
import {
  EditTagFormType,
  EditTagGroupFormType,
  integrationFormValuesToGqlInput,
  NgpVanIntegration,
} from "./types";

export default function Tags(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const snackbar = useSnackbar();
  const confirm = useConfirm();
  useBreadcrumbs([{ label: "Tags", path: subaccountLink("/tags") }]);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, TAG_SORTS[0].value)
  );

  const sortValue =
    TAG_SORTS.find(availableSort => availableSort.value === sort)?.value ||
    TAG_SORTS[0].value;

  const listTagsResult = useQuery<
    ListTagsAndGroupsWithIntegrationsQuery,
    ListTagsAndGroupsWithIntegrationsQueryVariables
  >(LIST_TAGS_AND_GROUPS_WITH_INTEGRATIONS, {
    variables: { subaccountId: subaccount.id },
  });

  const integrations = useIntegrations({
    service: IntegrationServiceType.Ngpvan,
    subaccountId: subaccount.id,
  });

  const tags = useMemo(
    () => listTagsResult.data?.listTags || [],
    [listTagsResult.data]
  );
  const tagGroups = useMemo(
    () => listTagsResult.data?.listTagGroups || [],
    [listTagsResult.data]
  );
  const allTagAndGroupNames = useMemo(() => {
    const tagNames = tags.map(tag => tag.name);
    const tagGroupNames = tagGroups.map(group => group.name);
    return [...tagNames, ...tagGroupNames];
  }, [tags, tagGroups]);

  const { data: dataPage, totalCount } = usePaginatedTags({
    tags,
    tagGroups,
    page,
    pageSize,
    search,
    sort: sortValue,
  });

  const [createTagDialogOpen, setCreateTagDialogOpen] = useState(false);

  const [createTag] = useMutation<
    CreateTagMutation,
    CreateTagMutationVariables
  >(CREATE_TAG, {
    refetchQueries: [
      {
        query: LIST_TAGS_AND_GROUPS_WITH_INTEGRATIONS,
        variables: { subaccountId: subaccount.id },
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleCreateTag = useCallback(
    async (formData: EditTagFormType) => {
      const { errors } = await createTag({
        variables: {
          input: {
            name: formData.name.trim(),
            subaccountId: subaccount.id,
            ngpVanIntegrations: integrationFormValuesToGqlInput(
              formData.integrations
            ),
          },
        },
      });

      if (errors) {
        throw errors;
      }

      snackbar.enqueueSnackbar(`Tag ${formData.name} created`);
      setCreateTagDialogOpen(false);
    },
    [createTag, subaccount, snackbar]
  );

  const [createTagGroup] = useMutation<
    CreateTagGroupMutation,
    CreateTagGroupMutationVariables
  >(CREATE_TAG_GROUP, {
    refetchQueries: [
      {
        query: LIST_TAGS_AND_GROUPS_WITH_INTEGRATIONS,
        variables: { subaccountId: subaccount.id },
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleCreateTagGroup = useCallback(
    async (formData: EditTagGroupFormType) => {
      const { errors } = await createTagGroup({
        variables: {
          input: {
            name: formData.name.trim(),
            subaccountId: subaccount.id,
            tags: formData.tags.map(tagFormData => ({
              name: tagFormData.name.trim(),
              ngpVanIntegrations: integrationFormValuesToGqlInput(
                tagFormData.integrations
              ),
            })),
          },
        },
      });

      if (errors) {
        throw errors;
      }
      snackbar.enqueueSnackbar(`Tag ${formData.name} created`);
      setCreateTagDialogOpen(false);
    },
    [createTagGroup, subaccount, snackbar]
  );

  const [editingTagOrGroupId, setEditingTagOrGroupId] = useState<{
    type: "Tag" | "TagGroup";
    id: string;
  } | null>(null);

  const editingTagOrGroup = useMemo<
    | { type: "Tag"; tag: TagDetail }
    | { type: "TagGroup"; group: TagGroupDetail }
    | null
  >(() => {
    if (!editingTagOrGroupId) {
      return null;
    }

    if (editingTagOrGroupId.type === "TagGroup") {
      const foundTagGroup = tagGroups.find(
        g => g.id === editingTagOrGroupId.id
      );
      return foundTagGroup ? { type: "TagGroup", group: foundTagGroup } : null;
    }

    const foundTag = tags.find(t => t.id === editingTagOrGroupId.id);
    return foundTag ? { type: "Tag", tag: foundTag } : null;
  }, [tags, tagGroups, editingTagOrGroupId]);

  const [editTag] = useMutation<EditTagMutation, EditTagMutationVariables>(
    EDIT_TAG,
    {
      refetchQueries: [
        {
          query: LIST_TAGS_WITH_INTEGRATIONS,
          variables: { input: { subaccountId: subaccount.id } },
        },
      ],
      awaitRefetchQueries: true,
    }
  );

  const handleEditTag = useCallback(
    async (formData: EditTagFormType) => {
      if (editingTagOrGroupId?.type !== "Tag") {
        console.warn("Not editing a tag");
        return;
      }

      const { errors } = await editTag({
        variables: {
          input: {
            tagId: editingTagOrGroupId.id,
            name: formData.name.trim(),
            ngpVanIntegrations: integrationFormValuesToGqlInput(
              formData.integrations
            ),
          },
        },
      });

      if (errors) {
        throw errors;
      }

      snackbar.enqueueSnackbar(`Tag ${formData.name} updated`);
      setEditingTagOrGroupId(null);
    },
    [editingTagOrGroupId, setEditingTagOrGroupId, editTag, snackbar]
  );

  const [editTagGroup] = useMutation<
    EditTagGroupMutation,
    EditTagGroupMutationVariables
  >(EDIT_TAG_GROUP, {
    refetchQueries: [
      {
        query: LIST_TAGS_WITH_INTEGRATIONS,
        variables: { input: { subaccountId: subaccount.id } },
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleEditTagGroup = useCallback(
    async (formData: EditTagGroupFormType) => {
      if (editingTagOrGroupId?.type !== "TagGroup") {
        console.warn("Not editing a tagGroup");
        return;
      }

      const { errors } = await editTagGroup({
        variables: {
          input: {
            tagGroupId: editingTagOrGroupId.id,
            name: formData.name.trim(),
            tags: formData.tags.map(tagFormData => ({
              id: tagFormData.id,
              name: tagFormData.name.trim(),
              ngpVanIntegrations: integrationFormValuesToGqlInput(
                tagFormData.integrations
              ),
            })),
          },
        },
      });

      if (errors) {
        throw errors;
      }
      snackbar.enqueueSnackbar(`Tag ${formData.name} updated`);
      setEditingTagOrGroupId(null);
    },
    [editingTagOrGroupId, setEditingTagOrGroupId, editTagGroup, snackbar]
  );

  const [deleteTags] = useMutation<
    DeleteTagsMutation,
    DeleteTagsMutationVariables
  >(DELETE_TAGS);

  const [deleteTagGroups] = useMutation<
    DeleteTagGroupsMutation,
    DeleteTagGroupsMutationVariables
  >(DELETE_TAG_GROUPS);

  const handleDeleteTag = useCallback(
    async (id: string) => {
      const tag = tags?.find(tag => tag.id === id);
      if (!tag) {
        return;
      }

      const { errors } = await deleteTags({
        variables: { input: { tagIds: [tag.id] } },
      });

      await listTagsResult.refetch().catch(console.error);

      if (errors) {
        snackbar.enqueueSnackbar(`Error deleting tag: ${errors[0].message}`, {
          variant: "error",
        });
        throw errors;
      } else {
        snackbar.enqueueSnackbar(`Tag ${tag.name} deleted`);
      }
    },
    [tags, listTagsResult, deleteTags, snackbar]
  );

  const handleDeleteTagGroup = useCallback(
    async (id: string) => {
      const group = tagGroups?.find(group => group.id === id);
      if (!group) {
        return;
      }

      const { errors } = await deleteTagGroups({
        variables: { input: { tagGroupIds: [group.id] } },
      });

      await listTagsResult.refetch().catch(console.error);

      if (errors) {
        snackbar.enqueueSnackbar(`Error deleting tag: ${errors[0].message}`, {
          variant: "error",
        });
        throw errors;
      } else {
        snackbar.enqueueSnackbar(`Tag ${group.name} deleted`);
      }
    },
    [tagGroups, listTagsResult, deleteTagGroups, snackbar]
  );

  const [deleteManyState, setDeleteManyState] = useState<DeleteManyState>({
    selecting: false,
  });

  const resetDeleteManyState = useCallback(
    () => setDeleteManyState({ selecting: false }),
    [setDeleteManyState]
  );

  // clear delete many selections when tags update
  useEffect(resetDeleteManyState, [tags, tagGroups, resetDeleteManyState]);

  const handleDeleteSelectedTagsAndGroups = useCallback(async () => {
    if (!deleteManyState.selecting) {
      return;
    }

    const selectedTags =
      tags?.filter(tag => deleteManyState.selectedIds.includes(tag.id)) || [];

    const selectedTagGroups =
      tagGroups?.filter(group =>
        deleteManyState.selectedIds.includes(group.id)
      ) || [];

    if (!selectedTags.length && !selectedTagGroups.length) {
      return;
    }

    const tagAndGroupNames = [
      ...selectedTags.map(tag => `"${tag.name}"`),
      ...selectedTagGroups.map(group => `"${group.name}"`),
    ]
      .sort()
      .join(", ");

    const { confirmed } = await confirm({
      title: "Delete Tags",
      content: `Are you sure you want to delete the following tags: ${tagAndGroupNames}?`,
    });
    if (!confirmed) {
      return;
    }

    const [{ errors: tagErrors }, { errors: groupErrors }] = await Promise.all([
      selectedTags.length
        ? deleteTags({
            variables: { input: { tagIds: selectedTags.map(tag => tag.id) } },
          })
        : Promise.resolve({ errors: null }),
      selectedTagGroups.length
        ? deleteTagGroups({
            variables: {
              input: { tagGroupIds: selectedTagGroups.map(group => group.id) },
            },
          })
        : Promise.resolve({ errors: null }),
    ]);

    await listTagsResult.refetch().catch(console.error);

    const allErrors = [...(tagErrors || []), ...(groupErrors || [])];
    if (allErrors.length) {
      snackbar.enqueueSnackbar(`Error deleting tags: ${allErrors[0].message}`, {
        variant: "error",
      });
      throw allErrors[0];
    } else {
      resetDeleteManyState();
      snackbar.enqueueSnackbar(
        `${plural(selectedTags.length + selectedTagGroups.length, "Tag", "tags")} deleted`
      );
    }
  }, [
    deleteManyState,
    resetDeleteManyState,
    tags,
    tagGroups,
    confirm,
    deleteTags,
    deleteTagGroups,
    listTagsResult,
    snackbar,
  ]);

  return (
    <PageContainer>
      <PageHeader>Tags</PageHeader>
      <TagListControls
        sort={sortValue}
        setSort={setSort}
        search={search}
        setSearch={newSearch => {
          setSearch(newSearch);
          setPage(0);
        }}
        onClickAdd={() => setCreateTagDialogOpen(true)}
        deleteManyState={deleteManyState}
        setDeleteManyState={setDeleteManyState}
        onDeleteSelected={handleDeleteSelectedTagsAndGroups}
      />
      <TableBox>
        <AsyncWrapper
          loading={listTagsResult.loading}
          errors={listTagsResult.error}
          data={listTagsResult.data}
        >
          {() => (
            <TagTable
              rows={dataPage}
              totalCount={totalCount}
              page={page}
              onChangePage={setPage}
              rowsPerPage={pageSize}
              onChangeRowsPerPage={newPageSize => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              onClickEdit={row => {
                setEditingTagOrGroupId({
                  type: row.__typename,
                  id: row.id,
                });
              }}
              onClickDelete={async row => {
                if (row.__typename === "Tag") {
                  await handleDeleteTag(row.id);
                } else {
                  await handleDeleteTagGroup(row.id);
                }
              }}
              deleteManyState={deleteManyState}
              onChangeDeleteManyState={setDeleteManyState}
            />
          )}
        </AsyncWrapper>
      </TableBox>

      {createTagDialogOpen && (
        <CreateTagOrGroupDialog
          open
          onClose={() => setCreateTagDialogOpen(false)}
          onCreateTag={handleCreateTag}
          onCreateTagGroup={handleCreateTagGroup}
          vanIntegrations={integrations.integrations as NgpVanIntegration[]}
          allTagNames={allTagAndGroupNames}
        />
      )}

      {editingTagOrGroup &&
        (editingTagOrGroup.type === "Tag" ? (
          <EditTagDialog
            open
            onClose={() => setEditingTagOrGroupId(null)}
            onSave={handleEditTag}
            vanIntegrations={integrations.integrations as NgpVanIntegration[]}
            tag={editingTagOrGroup.tag}
            allTagNames={allTagAndGroupNames}
          />
        ) : (
          <EditTagGroupDialog
            open
            onClose={() => setEditingTagOrGroupId(null)}
            onSave={handleEditTagGroup}
            vanIntegrations={integrations.integrations as NgpVanIntegration[]}
            tagGroup={editingTagOrGroup.group}
            allTagNames={allTagAndGroupNames}
          />
        ))}
    </PageContainer>
  );
}
